'use client';

import { CSSProperties, useEffect, useRef, useState } from 'react';
import styles from './ScrollRevealText.module.css';

/* Photon — one-time scroll-reveal text.
   Word-by-word opacity fade-in (no movement) when the wrapper crosses
   the threshold. Each word fades in with a small stagger so the line
   "types itself" without any vertical motion. One-time only — never
   re-runs and never fades back out. */
interface Props {
  text: string;
  className?: string;
  /** Fade duration per word, in ms. */
  duration?: number;
  /** Delay between consecutive words, in ms. */
  stagger?: number;
  /** IntersectionObserver threshold (0–1). */
  threshold?: number;
}

const ScrollRevealText = ({
  text,
  className,
  duration = 500,
  stagger = 90,
  threshold = 0.35,
}: Props) => {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setRevealed(true);
          io.disconnect();
        }
      },
      { threshold },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [threshold]);

  const words = text.split(' ');

  return (
    <span
      ref={ref}
      className={className ? `${styles.wrapper} ${className}` : styles.wrapper}
      aria-label={text}
    >
      {words.map((word, i) => (
        <span
          key={i}
          className={styles.word}
          data-revealed={revealed}
          style={
            {
              ['--reveal-duration']: `${duration}ms`,
              ['--reveal-delay']: `${i * stagger}ms`,
            } as CSSProperties
          }
          aria-hidden="true"
        >
          {word}
          {i < words.length - 1 ? ' ' : ''}
        </span>
      ))}
    </span>
  );
};

export default ScrollRevealText;
